import { ChevronLeft, MessagesSquare } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { Button } from '@/components/ui/button';
import { ConversationPane } from '@/pages/admin/conversations/ConversationPane';
import {
  CONVERSATION_TONES,
  useConversationLiveUpdates,
  useConversations,
} from '@/hooks/conversations';
import { formatInstant } from '@/lib/dates';
import { cn } from '@/lib/utils';
import {
  CONVERSATION_INBOX_FILTERS,
  type ConversationListItem,
} from '@shared/schemas/conversation';
import { DEFAULT_PAGE_SIZE, RECORD_SORTS } from '@shared/schemas/pagination';
import { SortSelect } from '@/components/ui/sort-select';
import type { RecordSort } from '@shared/schemas/pagination';

/*
 * /admin/conversations — the inbox, ROADMAP.md §9.5.
 *
 * Two panes, the same shape as the leads inbox, for the same reason: the office
 * works down a list and reads one thing at a time. The selected thread lives in
 * the URL (`?thread=…`) so it survives a refresh and can be sent to somebody.
 *
 * **"Needs filing" is a filter, not a status.** It means a thread attached to
 * neither a client nor a lead — somebody wrote in and nobody has said who they
 * are yet. Keeping it out of the status enum is what stops a thread being both
 * `open` and `triage` in a way nothing can represent.
 */

const ASSIGNMENT_FILTERS = [
  { value: '', label: 'Anyone' },
  { value: 'me', label: 'Assigned to me' },
  { value: 'unassigned', label: 'Unassigned' },
];

export function ConversationsPage() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('thread') ?? '';

  const [status, setStatus] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<RecordSort>('newest');

  const conversations = useConversations({ sort, page, status, search, assignedTo });

  // A reply arriving moves a badge and reorders the list, without a refresh.
  useConversationLiveUpdates();

  function select(id: string): void {
    const next = new URLSearchParams(params);
    if (id === '') next.delete('thread');
    else next.set('thread', id);
    setParams(next, { replace: true });
  }

  return (
    <AdminPage
      title="Conversations"
      description="Every message to and from a customer, in one place."
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* On a narrow screen one pane at a time: the list until something is
            picked, then the thread with a way back. */}
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col gap-3',
            selectedId === '' ? 'flex' : 'hidden lg:flex',
          )}
        >
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder="Search names, addresses, subjects and message text"
          />

          <FilterPills
            options={CONVERSATION_INBOX_FILTERS.map((filter) => ({
              value: filter.value,
              label: filter.label,
            }))}
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            label="Filter conversations by status"
          />

          <FilterPills
            options={ASSIGNMENT_FILTERS}
            value={assignedTo}
            onChange={(value) => {
              setAssignedTo(value);
              setPage(1);
            }}
            label="Filter conversations by who they are assigned to"
          />

          {/* The order control, in the parent rather than in ConversationList —
              `sort` and `setPage` live here, and the list is a child component
              that only receives what it renders. A select rather than clickable
              headers: this list is cards at every width. */}
          <div className="flex justify-end">
            <SortSelect
              label="Sort conversations"
              options={RECORD_SORTS}
              value={sort}
              onChange={(next) => {
                setSort(next);
                // Page 1, or you are on page 3 of a differently ordered list.
                setPage(1);
              }}
            />
          </div>

          <ConversationList
            query={conversations}
            selectedId={selectedId}
            onSelect={select}
            page={page}
            onPage={setPage}
          />
        </div>

        <div className={cn('min-w-0 flex-1', selectedId === '' ? 'hidden lg:block' : 'block')}>
          {selectedId === '' ? (
            <div className="border-ink-200 rounded-lg border bg-white">
              <EmptyState
                icon={MessagesSquare}
                title="Pick a conversation"
                hint="Choose one on the left to read it and reply."
              />
            </div>
          ) : (
            <>
              <Button
                variant="ghost"
                className="mb-2 lg:hidden"
                onClick={() => {
                  select('');
                }}
              >
                <ChevronLeft aria-hidden="true" className="mr-1 size-4" />
                All conversations
              </Button>
              <ConversationPane id={selectedId} />
            </>
          )}
        </div>
      </div>
    </AdminPage>
  );
}

function ConversationList({
  query,
  selectedId,
  onSelect,
  page,
  onPage,
}: {
  query: ReturnType<typeof useConversations>;
  selectedId: string;
  onSelect: (id: string) => void;
  page: number;
  onPage: (page: number) => void;
}) {
  /*
   * **A failed request is an error, never an empty state** — CLAUDE.md §4,
   * defect P22. "No conversations" over a request that simply failed is how
   * somebody concludes nobody has written to them.
   */
  if (query.isError) {
    return (
      <div className="border-ink-200 rounded-lg border bg-white">
        <ErrorState onRetry={() => void query.refetch()} />
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true">
        {[0, 1, 2, 3].map((row) => (
          <div key={row} className="h-20 animate-pulse rounded-lg bg-ink-100" />
        ))}
      </div>
    );
  }

  if (query.data.total === 0) {
    return (
      <div className="border-ink-200 rounded-lg border bg-white">
        <EmptyState
          icon={MessagesSquare}
          title="Nothing here"
          hint="No conversations match these filters."
        />
      </div>
    );
  }

  return (
    <>
      <ul className="flex flex-col gap-2">
        {query.data.data.map((item) => (
          <li key={item.id}>
            <ConversationRow
              item={item}
              selected={item.id === selectedId}
              onSelect={() => {
                onSelect(item.id);
              }}
            />
          </li>
        ))}
      </ul>

      <Pagination
        page={page}
        pageSize={DEFAULT_PAGE_SIZE}
        total={query.data.total}
        onPageChange={onPage}
      />
    </>
  );
}

function ConversationRow({
  item,
  selected,
  onSelect,
}: {
  item: ConversationListItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const unread = item.unreadCount > 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected}
      className={cn(
        'w-full rounded-lg border p-3 text-left transition-colors',
        selected
          ? 'border-bark-500 bg-honey-50/60'
          : 'border-ink-200 hover:border-bark-300 bg-white',
      )}
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className={cn('text-body text-ink-900', unread && 'font-semibold')}>
          {item.contactName ?? item.contactEmail}
        </span>
        {/*
          **"Clear and obvious that they are not customers"** — the owner's own
          words, 2026-08-07. On the row rather than only inside the thread,
          because the row is where somebody decides which one to open, and
          answering an applicant as though they were a customer is the mistake
          this badge exists to stop. Bark on honey rather than a grey chip: it
          has to read as a different *kind* of thing, not as another status.
        */}
        {item.kind === 'applicant' ? (
          <span className="text-caption text-bark-800 rounded-full bg-honey-100 px-2 py-0.5 font-medium">
            {item.kindLabel}
          </span>
        ) : null}
        {item.isTriage ? (
          <span className="text-caption text-ink-600 rounded-full bg-ink-100 px-2 py-0.5">
            Needs filing
          </span>
        ) : null}
        <span className="text-caption ml-auto text-ink-500">
          {formatInstant(item.lastMessageAt)}
        </span>
      </div>

      <p className={cn('mt-0.5 truncate text-meta text-ink-900', unread && 'font-medium')}>
        {item.subject}
      </p>

      {item.preview === null ? null : (
        <p className="text-caption mt-0.5 truncate text-ink-500">{item.preview}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <StatusBadge tone={CONVERSATION_TONES[item.status]} label={item.statusLabel} />
        {item.assignedToName === null ? null : (
          <span className="text-caption text-ink-500">{item.assignedToName}</span>
        )}
        {unread ? (
          <span className="text-caption ml-auto rounded-full bg-bark-700 px-2 py-0.5 text-white">
            {/* Zero is `0` and unread is never shown as a dash — CLAUDE.md §4. */}
            {item.unreadCount} new
          </span>
        ) : null}
      </div>
    </button>
  );
}
